import { useEffect, useMemo, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Loader2, ShieldCheck, Pencil, Trash2, ImageIcon,
  ExternalLink, Upload, Search, ShoppingBag, Filter, ArrowUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  catalogList, catalogUpsert, catalogDelete, catalogPickerIngredients,
} from "../lib/api";
import CatalogForm from "../components/admin/CatalogForm";
import { DEFAULTS } from "../components/admin/catalogMeta";

function affiliateCount(p) {
  return ["affiliate_amazon", "affiliate_sephora", "affiliate_ulta", "affiliate_target"]
    .filter((k) => p?.[k]).length;
}

// Coverage = mapped canonical ids ÷ cleaned ingredient tokens. Colour band:
// ≥90 green, 60–89 amber, <60 red; null (no ingredient text) is neutral.
function coverageTone(pct) {
  if (pct == null) return "bg-stone-50 border-stone-200 text-stone-400";
  if (pct >= 90) return "bg-emerald-50 border-emerald-200 text-emerald-800";
  if (pct >= 60) return "bg-amber-50 border-amber-200 text-amber-800";
  return "bg-red-50 border-red-200 text-red-800";
}

function ProductRow({ p, onEdit, onDelete }) {
  const aff = affiliateCount(p);
  return (
    <tr
      data-testid={`product-row-${p.id}`}
      className="border-b border-stone-100 hover:bg-stone-50/70"
    >
      <td className="py-2.5 px-3 w-14">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt=""
            className="h-10 w-10 rounded-lg object-cover bg-stone-100 border border-stone-200"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="h-10 w-10 rounded-lg bg-stone-100 border border-stone-200 inline-flex items-center justify-center">
            <ImageIcon className="h-4 w-4 text-stone-400" />
          </div>
        )}
      </td>
      <td className="py-2.5 px-3">
        <div className="font-semibold text-sm text-stone-900 truncate max-w-[280px]">
          {p.full_name || `${p.brand || ""} ${p.name || ""}`}
        </div>
        <div className="text-[11px] text-stone-500 mt-0.5 inline-flex items-center gap-2">
          <span>{p.brand || "—"}</span>
          <span>·</span>
          <span className="font-mono uppercase tracking-wider">{p.category || "—"}</span>
          {p.barcode_upc && (
            <>
              <span>·</span>
              <span className="font-mono text-[10px]">{p.barcode_upc}</span>
            </>
          )}
        </div>
      </td>
      <td className="py-2.5 px-3 text-xs text-stone-700 tabular-nums">
        {p.price_usd != null ? `$${Number(p.price_usd).toFixed(2)}` : "—"}
      </td>
      <td className="py-2.5 px-3">
        <span className="text-[11px] text-stone-600">
          {p.key_ingredient_ids?.length ?? 0} ingredient{(p.key_ingredient_ids?.length ?? 0) === 1 ? "" : "s"}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span
          data-testid={`product-coverage-${p.id}`}
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold tabular-nums ${coverageTone(p.coverage_pct)}`}
          title={p.coverage_pct == null ? "No ingredient text to measure" : `${p.coverage_mapped}/${p.coverage_tokens} tokens mapped`}
        >
          {p.coverage_pct == null ? "—" : `${p.coverage_pct}%`}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
          aff > 0 ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-stone-50 border-stone-200 text-stone-500"
        }`}>
          <ExternalLink className="h-2.5 w-2.5" /> {aff}
        </span>
      </td>
      <td className="py-2.5 px-3">
        {p.is_safe_alternative ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
            <ShieldCheck className="h-2.5 w-2.5" /> Safe alt
          </span>
        ) : (
          <span className="text-[11px] text-stone-400">—</span>
        )}
      </td>
      <td className="py-2.5 px-3 text-right">
        <div className="inline-flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(p)} data-testid={`product-edit-${p.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(p)} className="text-red-600 hover:bg-red-50" data-testid={`product-delete-${p.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [pickers, setPickers] = useState({ ingredients: [] });
  const [sortCov, setSortCov] = useState("none");   // none | asc | desc
  const [needsReview, setNeedsReview] = useState(false);
  const cycleSort = () => setSortCov((s) => (s === "none" ? "desc" : s === "desc" ? "asc" : "none"));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await catalogList("products", { q });
      setRows(data);
    } catch (e) {
      toast.error(`Failed to load products: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => {
    (async () => {
      try {
        const ing = await catalogPickerIngredients();
        setPickers({ ingredients: ing });
      } catch (_) { /* noop */ }
    })();
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startCreate = () => setEditing({ ...DEFAULTS.products });
  const startEdit = (p) => setEditing({ ...p });
  const cancel = () => setEditing(null);

  const save = async () => {
    try {
      await catalogUpsert("products", editing);
      toast.success("Product saved");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Soft-delete product "${p.full_name || p.name}"?`)) return;
    try {
      await catalogDelete("products", p.id, { hard: false });
      toast.success("Product deactivated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const total = rows.length;
  const safeAlts = useMemo(() => rows.filter((p) => p.is_safe_alternative).length, [rows]);
  const withAffiliate = useMemo(() => rows.filter((p) => affiliateCount(p) > 0).length, [rows]);
  const needsReviewCount = useMemo(
    () => rows.filter((p) => p.coverage_pct != null && p.coverage_pct < 90).length,
    [rows],
  );
  const displayRows = useMemo(() => {
    let r = needsReview ? rows.filter((p) => p.coverage_pct != null && p.coverage_pct < 90) : rows;
    if (sortCov !== "none") {
      r = [...r].sort((a, b) => {
        const av = a.coverage_pct == null ? -1 : a.coverage_pct;
        const bv = b.coverage_pct == null ? -1 : b.coverage_pct;
        return sortCov === "asc" ? av - bv : bv - av;
      });
    }
    return r;
  }, [rows, needsReview, sortCov]);

  return (
    <main
      data-testid="admin-products-page"
      className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-6xl">
        <AdminHeader />

        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Product Manager</h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              Add products, attach key ingredients, set affiliate links and safer-alternative flags.
              Use Bulk Import for CSV-style batch loads.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/import">
              <Button variant="outline" size="sm" data-testid="products-bulk-link">
                <Upload className="h-4 w-4 mr-1.5" /> Bulk import
              </Button>
            </Link>
            <Button size="sm" onClick={startCreate} data-testid="products-new-btn" className="bg-stone-900 hover:bg-stone-800 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> New product
            </Button>
          </div>
        </header>

        {/* Summary tiles */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          <div className="rounded-xl border border-stone-200 bg-white p-3 text-center" data-testid="products-summary-total">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold text-stone-600">Total</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5 text-stone-900">{total}</div>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="products-summary-safe">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold text-emerald-700">What to use instead</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5 text-emerald-900">{safeAlts}</div>
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center" data-testid="products-summary-affiliate">
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold text-amber-700">With affiliate</div>
            <div className="text-2xl font-bold tabular-nums mt-0.5 text-amber-900">{withAffiliate}</div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            data-testid="products-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by full name, brand, or barcode…"
            className="w-full h-11 pl-10 pr-3 rounded-xl border border-stone-200 bg-white focus:outline-none focus:ring-4 focus:ring-stone-100 focus:border-stone-300 transition-all"
          />
        </div>

        {/* Coverage filter */}
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <button
            data-testid="products-filter-needs-review"
            onClick={() => setNeedsReview((v) => !v)}
            data-active={needsReview ? "1" : "0"}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${needsReview ? "bg-red-600 border-red-600 text-white" : "bg-white border-stone-300 text-stone-600 hover:bg-stone-100"}`}
          >
            <Filter className="h-3.5 w-3.5" /> Needs review (&lt;90%)
            <span className={`tabular-nums rounded-full px-1.5 ${needsReview ? "bg-white/20" : "bg-stone-200/80"}`}>{needsReviewCount}</span>
          </button>
          <span className="text-xs text-stone-500" data-testid="products-shown-count">{displayRows.length} shown</span>
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-stone-400">
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            </div>
          ) : rows.length === 0 ? (
            <div data-testid="products-empty" className="p-12 text-center">
              <ShoppingBag className="h-10 w-10 text-stone-300 mx-auto" />
              <p className="mt-3 text-sm font-semibold text-stone-700">No products yet.</p>
              <p className="mt-1 text-xs text-stone-500">Add one manually or use bulk import.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
                <tr>
                  <th className="text-left py-2 px-3 w-14"></th>
                  <th className="text-left py-2 px-3">Product</th>
                  <th className="text-left py-2 px-3">Price</th>
                  <th className="text-left py-2 px-3">Ingredients</th>
                  <th className="text-left py-2 px-3">
                    <button
                      type="button"
                      data-testid="products-sort-coverage"
                      onClick={cycleSort}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-stone-800"
                      title="Mapped ingredients ÷ cleaned tokens — click to sort"
                    >
                      Coverage
                      {sortCov === "asc" ? " ↑" : sortCov === "desc" ? " ↓" : <ArrowUpDown className="h-3 w-3 opacity-50" />}
                    </button>
                  </th>
                  <th className="text-left py-2 px-3">Affiliates</th>
                  <th className="text-left py-2 px-3">Safe?</th>
                  <th className="text-right py-2 px-3"></th>
                </tr>
              </thead>
              <tbody data-testid="products-rows">
                {displayRows.length === 0 ? (
                  <tr data-testid="products-filter-empty">
                    <td colSpan={8} className="p-10 text-center text-sm text-stone-500">
                      No products match this filter.
                    </td>
                  </tr>
                ) : (
                  displayRows.map((p) => (
                    <ProductRow key={p.id} p={p} onEdit={startEdit} onDelete={remove} />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Edit / Create modal */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && cancel()}>
        <DialogContent
          data-testid="products-edit-dialog"
          className="max-w-2xl max-h-[88vh] overflow-y-auto"
        >
          <DialogTitle>{editing?.id && rows.find((r) => r.id === editing.id) ? "Edit product" : "New product"}</DialogTitle>
          <DialogDescription>
            All fields below land in the products collection. Affiliate URLs unlock pricing rotation
            in the safer-alternatives engine.
          </DialogDescription>
          {editing && (
            <CatalogForm
              slug="products"
              value={editing}
              onChange={setEditing}
              mediaPickers={pickers}
            />
          )}
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
            <Button variant="ghost" onClick={cancel} data-testid="products-cancel">Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="products-save">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
